import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useCallback,
} from 'react';

const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

interface AcademicsFiltersState {
  academicYear: string;
  grade: string;
  setAcademicYear: (year: string) => void;
  setGrade: (grade: string) => void;
}

const AcademicsFiltersContext = createContext<
  AcademicsFiltersState | undefined
>(undefined);

interface AcademicsFiltersProviderProps {
  children: ReactNode;
}

export const AcademicsFiltersProvider: React.FC<AcademicsFiltersProviderProps> = ({
  children,
}) => {
  const [academicYear, setAcademicYearState] = useState(getCurrentAcademicYear);
  const [grade, setGradeState] = useState('');

  const setAcademicYear = useCallback((year: string) => {
    setAcademicYearState(year);
  }, []);

  const setGrade = useCallback((g: string) => {
    setGradeState(g);
  }, []);

  const value = useMemo(
    () => ({
      academicYear,
      grade,
      setAcademicYear,
      setGrade,
    }),
    [academicYear, grade, setAcademicYear, setGrade]
  );

  return (
    <AcademicsFiltersContext.Provider value={value}>
      {children}
    </AcademicsFiltersContext.Provider>
  );
};

export const useAcademicsFiltersContext = (): AcademicsFiltersState => {
  const context = useContext(AcademicsFiltersContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsFiltersContext must be used within AcademicsFiltersProvider'
    );
  }
  return context;
};
